import React, { useEffect, useRef, useState } from 'react';
import ArtActivityList from './ArtActivityList';
import ArtActivityWorkspace from './ArtActivityWorkspace';
import { ART_VIEWS } from '../../../data/learningZoneMockData';

/**
 * Computer Apps > Artweaver > Beginners / Intermediate (`screen.preset`, an ART_VIEWS id; Art Therapy's
 * rows until 2026-10-26): a numbered list of the
 * art lessons admins placed there. Open ArtWeaver on a row swaps the list for that lesson's drawing
 * page, and Back to Activities swaps it back; a quiz started from a row (`quiz`) takes the list's
 * place until it is closed. CourseContentView keeps the behaviour and passes it in:
 * `lessons` (the row's), `onOpenItem(item, lesson)` and `quiz`. Rendered inside
 * LearningZonePage's container-query wrapper, which the drawing board's layout also keys off.
 */
export default function ArtTherapyView({ title, screen = {}, lessons, onOpenItem, quiz }) {
  const { title: listTitle = title, description = '' } = ART_VIEWS[screen.preset] || {};
  const [activeLessonId, setActiveLessonId] = useState(null);
  const headingRef = useRef(null);
  const shownLessonId = useRef(activeLessonId);

  const activeLesson = lessons.find((lesson) => lesson.id === activeLessonId);

  // The list and a drawing page are different heights, so on every swap bring
  // the heading back to the top instead of leaving the scroll wherever it was.
  useEffect(() => {
    if (shownLessonId.current === activeLessonId) return;
    shownLessonId.current = activeLessonId;
    headingRef.current?.scrollIntoView?.({ block: 'start' });
  }, [activeLessonId]);

  return (
    <>
      <div ref={headingRef} className="mb-6 flex items-center gap-4">
        <span className="text-5xl leading-none" aria-hidden="true">
          🎨
        </span>
        <div>
          <h1 className="text-[28px] font-extrabold leading-tight text-slate-900">Artweaver</h1>
          <p className="text-[15px] font-medium text-gray-500">Express. Create. Feel Good!</p>
        </div>
      </div>

      <section aria-label={listTitle} className="mb-6 rounded-2xl border border-gray-100 bg-white/70 p-4 sm:p-6">
        {quiz ||
          (activeLesson ? (
            <ArtActivityWorkspace
              key={activeLesson.id}
              lesson={activeLesson}
              viewTitle={listTitle}
              onBack={() => setActiveLessonId(null)}
              onOpenItem={onOpenItem}
            />
          ) : (
            <>
              <div className="mb-5 flex items-center gap-3">
                <span className="text-4xl leading-none" aria-hidden="true">
                  🎨
                </span>
                <div>
                  <h2 className="text-2xl font-extrabold leading-tight text-slate-900">{listTitle}</h2>
                  {description && <p className="text-sm font-medium text-gray-500">{description}</p>}
                </div>
              </div>

              {lessons.length ? (
                <>
                  <ArtActivityList lessons={lessons} onOpenActivity={setActiveLessonId} onOpenItem={onOpenItem} />
                </>
              ) : (
                <p className="py-6 text-center text-[15px] font-semibold text-gray-500">No lessons have been added here yet.</p>
              )}
            </>
          ))}
      </section>
    </>
  );
}
